import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
// Lange taken draaien op de server; we vragen elke 2,5 s de status op (max ~5 min).
const runJob = async (path, body) => {
  const { data } = await axios.post(`${API}${path}`, body, { headers: auth() });
  for (let i = 0; i < 120; i += 1) {
    await new Promise((r) => setTimeout(r, 2500));
    const { data: job } = await axios.get(`${API}/admin/motoscout/jobs/${data.job_id}`, { headers: auth() });
    if (job.status === 'done') return job.result;
    if (job.status === 'error') throw new Error(job.error);
  }
  throw new Error('Duurt te lang, probeer het opnieuw');
};
const errMsg = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

const FIELDS = [['brand', 'Merk'], ['model', 'Model'], ['year', 'Jaar'], ['mileage', 'Km-stand'], ['color', 'Kleur'], ['price_chf', 'Prijs MotoScout (CHF)'], ['markup_chf', 'Opslag (CHF)'], ['seller', 'Verkoper (alleen admin)']];

// Plak een MotoScout24-advertentie → AI vult gegevens → concept-motor met opslag.
export const MotoScoutImportDialog = ({ open, onClose, onCreated }) => {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [showText, setShowText] = useState(false);

  const reset = () => { setText(''); setUrl(''); setForm(null); setImages([]); setShowText(false); };
  const close = () => { reset(); onClose(); };

  const analyse = async (t = text, u = url) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/motoscout/parse`, { text: t, url: u }, { headers: auth() });
      setForm(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyse mislukt');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const data = await runJob('/admin/motoscout/drafts', { ...form, image_urls: images.filter((i) => i.on).map((i) => i.url) });
      toast.success(`${form.brand} ${form.model} opgeslagen als concept (${data.images.length} foto's)`);
      reset();
      onCreated();
    } catch (e) {
      toast.error(errMsg(e, 'Opslaan mislukt'));
    } finally {
      setBusy(false);
    }
  };

  const fetchLink = async () => {
    setBusy(true);
    try {
      const data = await runJob('/admin/motoscout/fetch', { url });
      setImages((data.images || []).map((u) => ({ url: u, on: true })));
      setForm(data);
    } catch (e) {
      toast.error(errMsg(e, 'Ophalen mislukt, probeer opnieuw'));
    } finally {
      setBusy(false);
    }
  };

  const toggleImg = (i) => setImages(images.map((im, j) => (j === i ? { ...im, on: !im.on } : im)));
  const total = (Number(form?.price_chf) || 0) + (Number(form?.markup_chf) || 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl" data-testid="motoscout-import-dialog">
        <DialogHeader>
          <DialogTitle>MotoScout24 importeren als concept</DialogTitle>
          <DialogDescription>
            Plak de link van de advertentie en klik op Ophalen. Foto's, bouwjaar, km en prijs (+ opslag) worden automatisch ingevuld. De omschrijving wordt standaard: motortype, bouwjaar, km en "Ab service".
          </DialogDescription>
        </DialogHeader>
        {!form ? (
          <div className="space-y-2">
            <label className="text-sm font-semibold text-zinc-800">Plak MotoScout-link</label>
            <Input placeholder="https://www.motoscout24.ch/de/d/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid="motoscout-url" />
            {busy && !showText && (
              <p className="text-sm text-purple-700 flex items-center gap-2" data-testid="motoscout-fetching">
                <Loader2 className="w-4 h-4 animate-spin" /> Advertentie wordt opgehaald (30 sec tot 3 min)…
              </p>
            )}
            <button type="button" onClick={() => setShowText(!showText)} className="text-xs text-zinc-500 underline" data-testid="motoscout-toggle-text">
              Lukt het niet? Plak dan de tekst van de advertentie
            </button>
            {showText && (
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7}
                        placeholder="Plak hier de tekst van de advertentie…"
                        className="w-full text-sm border border-zinc-300 rounded-md p-2" data-testid="motoscout-text" />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="text-xs text-zinc-600">{label}
                <Input value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`motoscout-field-${k}`} />
              </label>
            ))}
            <label className="text-xs text-zinc-600 col-span-2">Bronlink
              <Input value={form.source_url || ''} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="motoscout-field-source_url" />
            </label>
            {images.length > 0 && (
              <div className="col-span-2" data-testid="motoscout-images">
                <div className="text-xs text-zinc-600 mb-1">Foto's ({images.filter((i) => i.on).length}/{images.length}) · tik om uit te zetten</div>
                <div className="grid grid-cols-5 gap-1 max-h-40 overflow-y-auto">
                  {images.map((im, i) => (
                    <button key={im.url} type="button" onClick={() => toggleImg(i)} data-testid={`motoscout-image-${i}`}
                            className={`relative aspect-square rounded overflow-hidden border-2 ${im.on ? 'border-green-500' : 'border-zinc-200 opacity-40'}`}>
                      <img src={im.url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="col-span-2 bg-green-50 border border-green-200 rounded-lg p-2.5 text-sm" data-testid="motoscout-total">
              Verkoopprijs op het platform: <b>CHF {total.toLocaleString('de-CH')}</b>
            </div>
          </div>
        )}
        <DialogFooter>
          {form && <Button variant="outline" onClick={() => setForm(null)} disabled={busy}>Terug</Button>}
          {!form ? (
            showText ? (
              <Button onClick={() => analyse()} disabled={busy || text.trim().length < 30} className="bg-purple-600 hover:bg-purple-700" data-testid="motoscout-analyse-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Tekst analyseren
              </Button>
            ) : (
              <Button onClick={fetchLink} disabled={busy || !url.includes('motoscout24.ch')} className="bg-purple-600 hover:bg-purple-700" data-testid="motoscout-fetch-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Ophalen
              </Button>
            )
          ) : (
            <Button onClick={save} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="motoscout-save-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{busy ? "Foto's opslaan…" : 'Opslaan als concept'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
